// The push service worker (public/sw.js) posts {kind: "attention.action"}
// to every open window when the user clicks an OS notification. Until this
// listener existed that message went nowhere: the click navigated the tab,
// but the notifications feed and the Today attention state kept their stale
// caches. Handling it here means a push click-through lands on data that
// already reflects the notification it came from.

import { analyticsEvents, trackChallengeNotification } from "@healthos/analytics";
import { queryKeys } from "@healthos/client-data";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { NOTIFICATIONS_QUERY_KEY } from "../api/notifications";
import { analytics } from "../lib/analytics";
import { pushOpenedProperties } from "../pages/ambient/microstep-analytics";

interface ServiceWorkerAttentionMessage {
  kind: "attention.action";
  action?: string;
  data?: Record<string, unknown>;
}

function isAttentionActionMessage(data: unknown): data is ServiceWorkerAttentionMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { kind?: unknown }).kind === "attention.action"
  );
}

/**
 * Pure handler, separated from the effect so it is testable without a
 * ServiceWorkerContainer. Returns true when the message was recognised
 * and the caches were invalidated.
 */
export function handleServiceWorkerMessage(queryClient: QueryClient, data: unknown): boolean {
  if (!isAttentionActionMessage(data)) return false;
  void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: queryKeys.ambient });
  const pushOpened = pushOpenedProperties(data.data);
  if (pushOpened) analytics.track(analyticsEvents.microstepPushOpened, pushOpened);
  trackChallengeNotification(analytics.track, "opened", data.data, "push");
  return true;
}

export function useServiceWorkerNotificationSync(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const container = navigator.serviceWorker;
    const onMessage = (event: MessageEvent) => {
      handleServiceWorkerMessage(queryClient, event.data);
    };
    container.addEventListener("message", onMessage);
    return () => container.removeEventListener("message", onMessage);
  }, [queryClient]);
}
